import { useEffect, useState } from "react";
import { AdminCommunities, AdminHealth, AdminOverview, AdminRevenue, AdminTrends, AdminUsers } from "./admin";
import { Assistance } from "./assistance";
import { AuthScreen } from "./auth";
import { Sidebar, TopBar, isCapacitorNative } from "./components";
import { CommunityCalendar } from "./events";
import { BylawsLibrary, CommunityDirectory, HomeownerChat, HomeownerHistory } from "./homeowner";
import { ManagerBylaws, ManagerDashboard, ManagerRoster } from "./manager";
import { Marketplace } from "./marketplace";
import { Messages } from "./messages";
import { NotificationsBell } from "./notifications";
import { OAuthCallback } from "./oauth";
import { PmcPortfolio } from "./pmc";
import { PricingPage } from "./pricing";
import { initPushIfPermitted, promptPushOnMessages, unregisterPush } from "./push";
import { ProjectBuilder } from "./projects";
import { MyQuota } from "./quota";
import { Services } from "./services";
import { Settings } from "./settings";
import { SubCommunities } from "./sub_communities";
import { TownSquare } from "./town_square";
import { TweakColor, TweakRadio, TweakSection, TweakSelect, TweakToggle, TweaksPanel, useTweaks } from "./tweaks-panel";

// Main router + role switcher + tweaks panel.

function routeTitles(user) {
  const community = user?.community?.name || "your community";
  return {
    // homeowner
    chat: ["Check the Compass", `${community} bylaws`],
    history: ["My conversations", "Private to you"],
    bylaws: ["Bylaws library", "Read your community's documents"],
    directory: ["Community directory", `Your neighbors at ${community}`],
    // manager
    dashboard: [community, "Manager overview"],
    "bylaws-mgmt": ["Bylaws", "Upload & version management"],
    roster: ["Roster", ""],
    // PMC (company_admin only)
    portfolio: ["Portfolio", `All HOAs managed by ${user?.company?.name || "your company"}`],
    // admin
    "admin-overview": ["Operator overview", ""],
    "admin-health": ["Health", "System + queue + worklist"],
    "admin-communities": ["Communities", "All HOAs across the platform"],
    "admin-users": ["Users", "Every account on the platform"],
    "admin-revenue": ["Revenue", "MRR, ARR, margin"],
    "admin-trends": ["Trends", "System-wide activity"],
    // events (shared)
    events: ["Calendar", `Upcoming at ${community}`],
    marketplace: ["Marketplace", `For sale in ${community}`],
    services: ["Services", `Neighbors offering help in ${community}`],
    assistance: ["Request Assistance", `Neighbors asking for help in ${community}`],
    projects: ["Project Builder", `ARC requests in ${community}`],
    quota: ["Usage limits", "Your hourly Compass + community polish meters"],
    messages: ["Messages", `Conversations in ${community}`],
    "town-square": ["Town Square", `Discussions in ${community}`],
    "sub-communities": ["Sub-Communities", `Interest groups in ${community}`],
    // shared
    settings: ["Settings", "Your profile and account"],
  };
}

const DEFAULT_ROUTE = {
  homeowner: "chat",
  manager: "dashboard",
  admin: "admin-overview",
};

function App() {
  const [t, setTweak] = useTweaks(window.TWEAK_DEFAULTS);
  const [auth, setAuth] = useState(null); // null = login screen; or "homeowner"/"manager"/"admin"
  const [user, setUser] = useState(null);
  const [route, setRoute] = useState("chat");
  // Counter that bumps every time the sidebar nav fires for the
  // ALREADY-active route — used as a `key` on routes whose inner
  // component carries view-state of its own (Town Square's list /
  // detail / new). Without this, clicking the sidebar Town Square
  // entry while already on the detail view is a no-op because the
  // route hasn't changed; the component instance is preserved and
  // the inner `view` state stays at "detail".
  const [navTick, setNavTick] = useState(0);
  const navTo = (r) => {
    // Sub-community deep-link nav items are encoded as
    // "sub-community-<public_id>" so they can sit alongside top-level
    // route ids in the sidebar without a separate channel. Pluck the
    // id, set the deep-link state, and route to the regular
    // "sub-communities" page — SubCommunities reads `openId` and
    // jumps to the detail view.
    if (typeof r === "string" && r.startsWith("sub-community-")) {
      const publicId = r.slice("sub-community-".length);
      setOpenSubCommunityId(publicId);
      if (route === "sub-communities") setNavTick((n) => n + 1);
      setRoute("sub-communities");
      return;
    }
    // Plain "sub-communities" (the "All Communities" entry) clears
    // any prior deep-link so the list view shows.
    if (r === "sub-communities") {
      setOpenSubCommunityId(null);
    }
    if (r === route) setNavTick((n) => n + 1);
    setRoute(r);
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  // Flipped on by the `cc:session-expired` window event that api.jsx
  // fires when an authenticated request comes back 401. Routes the
  // user back to the auth screen with a banner instead of an opaque
  // "Request failed (401)" toast mid-task.
  const [sessionExpired, setSessionExpired] = useState(false);
  // Bootstrap /me failed for a non-auth reason (offline, 5xx, timeout).
  // The stored token is kept — wiping a valid 7-day JWT because the
  // app was opened in airplane mode forced a needless re-login on the
  // iOS shell (issue #410). Renders a retry screen instead of login.
  const [bootstrapError, setBootstrapError] = useState(false);
  // When the user opens a past conversation from History, stash the id so
  // HomeownerChat can preload it. Cleared after the chat picks it up.
  const [openChatSession, setOpenChatSession] = useState(null);
  // When a chat citation pill / source-panel entry is tapped, the chat
  // sets this to the bylaw_document_id and switches to the "bylaws"
  // route. BylawsLibrary auto-opens that doc on mount.
  const [openBylawDoc, setOpenBylawDoc] = useState(null);

  // Sub-community deep-link. When the sidebar nav-clicks a specific
  // joined group ("sub-community-<public_id>" item id), we set this to
  // the public_id and switch to the "sub-communities" route. The
  // SubCommunities component reads it on mount + on changes to jump
  // straight into the detail view; nav-clicking "All Communities"
  // clears it back to null. Same pattern as openBylawDoc above.
  const [openSubCommunityId, setOpenSubCommunityId] = useState(null);

  // Cross-route nav for the messages page. Set by callers like the
  // marketplace 'Message seller' button; Messages reads it on mount
  // and clears via onContextHandled.
  const [messagesContext, setMessagesContext] = useState(null);

  // Cross-route nav target for the Settings page tabs. Set when a
  // notification or banner asks for a specific tab (e.g. billing
  // notifications -> 'billing'). Cleared after Settings reads it.
  const [settingsInitialTab, setSettingsInitialTab] = useState(null);

  // Public pricing page (signed-out only). ?page=pricing on the URL
  // opens it on first load so the link is shareable; the "See pricing"
  // link on AuthScreen also flips this state. authInitialTab lets
  // PricingPage's "Onboard our HOA" CTA drop the user back onto the
  // right signup tab when they close the pricing page.
  const [showPricing, setShowPricing] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return new URL(window.location.href).searchParams.get("page") === "pricing";
    } catch (_) { return false; }
  });
  const [authInitialTab, setAuthInitialTab] = useState(null);

  // Email-verification token consumer at app level. Runs on every
  // mount regardless of auth state because the email link can be
  // clicked by:
  //   * a freshly-signed-up user who isn't logged in (AuthScreen
  //     route renders, but the token still needs to be POST'd —
  //     AuthScreen has its own handler for that case so the
  //     verify-result tab shows the outcome)
  //   * a user who's already authenticated in another browser tab
  //     or as another account in the same browser — in that case
  //     AuthScreen never renders and the token would be lost.
  //     Codex P2 on PR #142 round 9 flagged the latter path.
  // The endpoint is unauthenticated and idempotent for second
  // consumption (returns invalid_or_expired) so calling it from
  // both places is safe.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let token;
    try {
      token = new URL(window.location.href).searchParams.get("verify_token");
    } catch (_) {
      return;
    }
    if (!token) return;
    // Only consume here when a session is already active. When the
    // user is signed out, AuthScreen's effect takes the token + shows
    // the verify-result tab — we don't want to fire twice and have
    // the AuthScreen one hit invalid_or_expired.
    if (!auth) return;
    // Strip the token before the POST so a refresh on the authed app
    // doesn't loop.
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("verify_token");
      window.history.replaceState({}, "", u.toString());
    } catch (_) { /* ignore */ }
    const Api = window.Api;
    if (!Api) return;
    Api.verifyEmail({ token })
      .then(() => {
        // Logged-in users don't get a routing event — the token's
        // account may not even be the one they're signed in as.
        // Console-log so dev can see; no toast UI in this commit.
        console.info("verify_token consumed at app level (already authenticated)");
      })
      .catch(() => { /* swallow — auth-screen path covers user-facing recovery */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // Sync showPricing with the URL when the browser back/forward
  // buttons replay history. openPricing pushes ?page=pricing onto
  // the stack, so Back removes that entry and fires popstate; without
  // this listener React state would stay on PricingPage while the URL
  // returned to the auth screen, stranding the user. (Codex PR #107
  // P2 follow-up.)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPopState = () => {
      try {
        const isPricing = new URL(window.location.href).searchParams.get("page") === "pricing";
        setShowPricing(isPricing);
      } catch (_) { /* ignore */ }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Session-expired handling. api.jsx fires `cc:session-expired` when
  // an authenticated request returns 401 (token expired, revoked, or
  // user soft-deleted server-side). Drop back to the auth screen with
  // a banner so the user understands why their action failed instead
  // of seeing "Request failed (401)" inline on whatever form they
  // were submitting.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onExpired = () => {
      setUser(null);
      setAuth(null);
      setRoute("chat");
      setMenuOpen(false);
      setOpenChatSession(null);
      setMessagesContext(null);
      setSettingsInitialTab(null);
      setSessionExpired(true);
    };
    window.addEventListener("cc:session-expired", onExpired);
    return () => window.removeEventListener("cc:session-expired", onExpired);
  }, []);

  // Restore a saved session on first load. If the stored token is still
  // valid the server returns the user; otherwise we drop straight to login.
  useEffect(() => {
    const Api = window.Api;
    if (!Api || !Api.getToken()) {
      setBootstrapping(false);
      return;
    }
    let cancelled = false;
    // Routes are React state (no react-router), so a full page reload
    // — like the one Stripe Checkout triggers on return — drops the
    // user back to DEFAULT_ROUTE[role]. The PmcPortfolio (and any
    // future flow that hands off to Stripe) appends ?route=<id> to
    // its success/cancel URLs so we can restore the right view here.
    let restoredRoute = null;
    let restoredSettingsTab = null;
    try {
      const params = new URL(window.location.href).searchParams;
      const r = params.get("route");
      if (r && /^[a-z][a-z0-9-]{1,40}$/.test(r)) {
        restoredRoute = r;
      }
      // Used by the OAuth link-success redirect to drop the user on
      // the Security & sign-in tab where the new Linked Accounts list
      // lives. Same allowlist shape as the route param to refuse
      // garbage values.
      const st = params.get("settings_tab");
      if (st && /^[a-z][a-z0-9-]{1,40}$/.test(st)) {
        restoredSettingsTab = st;
      }
    } catch (_) { /* ignore */ }
    Api.me()
      .then((me) => {
        if (cancelled) return;
        const role = Api.uiRole(me.role);
        setUser(me);
        setAuth(role);
        setRoute(restoredRoute || DEFAULT_ROUTE[role]);
        if (restoredSettingsTab) setSettingsInitialTab(restoredSettingsTab);
      })
      .catch((err) => {
        if (cancelled) return;
        // 401 means the token really is dead — apiFetch already
        // cleared the session via _emitSessionExpired, so falling
        // through to the login screen is correct. Anything else
        // (network failure, 5xx, 429) is transient: keep the stored
        // token and offer a retry instead of destroying a valid
        // session (issue #410).
        if (err && err.status === 401) return;
        setBootstrapError(true);
      })
      .finally(() => { if (!cancelled) setBootstrapping(false); });
    return () => { cancelled = true; };
  }, []);

  // iOS push: once logged in, silently (re)register if the user already
  // granted notification permission — refreshes the APNs token and POSTs
  // it to the backend. The permission PROMPT itself is deferred to the
  // first Messages open (next effect). No-op off the native iOS shell.
  useEffect(() => {
    if (auth) initPushIfPermitted();
  }, [auth]);

  // Ask for push permission the first time the user opens Messages — a
  // moment where push obviously matters, and far better opt-in than a
  // cold prompt on launch. Self-guards to once-ever per device.
  useEffect(() => {
    if (auth && route === "messages") promptPushOnMessages();
  }, [auth, route]);

  // On mobile, the side nav is a hamburger-triggered drawer rather than
  // an always-visible column. Without this effect a user who just
  // signed in (or refreshed their session) lands on the chat with no
  // visible nav and has to hunt for the hamburger icon. Auto-open the
  // drawer once on each fresh landing — initial login, session
  // restore, or role switch (the role-switch case incidentally shows
  // them the new role's nav items). The drawer is mobile-only via CSS;
  // on desktop the sidebar is always visible and menuOpen has no
  // visible effect, so this is a no-op there.
  useEffect(() => {
    if (auth) setMenuOpen(true);
  }, [auth]);

  // Keep the sidebar Messages badge live from any page. Tiny endpoint,
  // 30s poll, plus an immediate refresh when the tab regains focus so
  // unlocking a phone shows the right count without a manual refresh.
  useEffect(() => {
    if (!user) return;
    const Api = window.Api;
    let cancelled = false;
    const refresh = async () => {
      try {
        const data = await Api.messagesUnreadCount();
        if (cancelled) return;
        setUser((cur) => (
          cur && cur.unread_messages_count !== data.count
            ? { ...cur, unread_messages_count: data.count }
            : cur
        ));
      } catch (_) { /* ignore */ }
    };
    refresh();
    const i = setInterval(refresh, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(i);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line
  }, [user?.id]);

  // Apply tweak-driven theme attributes to <html>
  const PALETTE_MAP = {
    "#c25e3a": "terracotta",
    "#2d5d4f": "forest",
    "#1f1a14": "ink",
    "#6b4c8a": "dusk",
  };
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", t.dark ? "dark" : "light");
    const hero = Array.isArray(t.palette) ? t.palette[0] : t.palette;
    document.documentElement.setAttribute("data-palette", PALETTE_MAP[hero?.toLowerCase?.()] || "terracotta");
    document.documentElement.setAttribute("data-fontpair", t.fontPair || "newsreader-geist");
  }, [t.dark, t.palette, t.fontPair]);

  const login = (role, nextUser) => {
    setSessionExpired(false);
    // A bootstrap /me failure on a stale token may have flagged the
    // retry screen while an OAuth callback was completing — a fresh
    // login supersedes it (codex round-2 P2 on PR #422).
    setBootstrapError(false);
    setUser(nextUser || null);
    setAuth(role);
    setRoute(DEFAULT_ROUTE[role]);
    // The /api/auth/login response is intentionally slim (no
    // memberships, no community block) — fetch /me right away so the
    // sidebar community switcher, badges, and trial banner have the
    // full payload immediately instead of waiting for the first
    // page-level refresh.
    if (window.Api?.getToken()) {
      window.Api.me().then((me) => setUser(me)).catch(() => {});
    }
  };
  const logout = () => {
    // Drop this device's push token first so a shared device stops
    // receiving the signed-out user's notifications.
    unregisterPush();
    window.Api?.logout();
    setUser(null);
    setAuth(null);
    // Same teardown the session-expired handler does — without
    // closing the mobile drawer the slide-in covers the AuthScreen
    // and the user thinks the tap did nothing (especially obvious
    // in the iOS Capacitor build where the drawer is opaque).
    setMenuOpen(false);
    setRoute("chat");
    setOpenChatSession(null);
    setMessagesContext(null);
    setSettingsInitialTab(null);
  };

  const switchCommunity = async (community_id) => {
    try {
      const updated = await window.Api.switchCommunity({ community_id });
      setUser(updated);
      // Switching can change role (manager-here vs homeowner-there); reset
      // the route to the new role's default so the user doesn't land on a
      // page they can't see anymore.
      const nextRole = window.Api.uiRole(updated.role);
      setAuth(nextRole);
      setRoute(DEFAULT_ROUTE[nextRole]);
    } catch (err) {
      console.warn("switchCommunity failed:", err);
    }
  };

  const switchRole = (role) => {
    setAuth(role);
    setRoute(DEFAULT_ROUTE[role]);
  };

  // Open / close the public pricing page. Keeps the URL in sync so the
  // page is shareable and the browser back button works the way you'd
  // expect (closing pricing removes ?page=pricing).
  const openPricing = () => {
    setShowPricing(true);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("page", "pricing");
      window.history.pushState({}, "", url.toString());
    } catch (_) { /* ignore */ }
  };
  const closePricing = (tabHint) => {
    setShowPricing(false);
    if (tabHint === "community" || tabHint === "login" || tabHint === "homeowner") {
      setAuthInitialTab(tabHint);
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("page");
      window.history.replaceState({}, "", url.toString());
    } catch (_) { /* ignore */ }
  };

  // ─── Auth state ──────────────────────────────────────────────────────────
  if (bootstrapping) {
    return <div className="scroll empty" style={{ padding: 40 }}>Loading…</div>;
  }
  // OAuth callback page — rendered before the auth-screen branch
  // because the user is anonymous when arriving from a login-mode
  // callback. For link-mode (authenticated) the page handles its own
  // post-link redirect back to "/?route=settings" so the normal app
  // flow takes over after a full reload. Must also render before the
  // bootstrap-error branch below: the callback completes via the
  // auth:false /oauth flows independently of restoring any stored
  // session, so a transient /me failure on a stale token must not
  // strand the user on the retry screen mid-OAuth (codex P2 on
  // PR #422).
  if (typeof window !== "undefined" && window.location.pathname === "/oauth-callback") {
    return (
      <>
        <OAuthCallback onLogin={login} />
        <Tweaks t={t} setTweak={setTweak} />
      </>
    );
  }
  // Gated on !auth as well: once any login succeeds (e.g. the OAuth
  // finalize flow on /oauth-callback, which renders above this), a
  // stale bootstrap failure must not mask the live session.
  if (bootstrapError && !auth) {
    return (
      <div className="scroll empty" style={{ padding: 40, textAlign: "center" }}>
        <p>Couldn't reach the server. Check your connection and try again.</p>
        <p>
          <button
            className="btn btn--primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </p>
        <p>
          {/* Escape hatch (codex P2 on PR #422): if /me fails
              persistently for THIS token (user-specific 500, long
              rate limit), endless retries with the same token would
              soft-lock the app — localStorage surgery being the only
              way out. Signing out clears the session and reloads
              into the normal login screen. */}
          <button
            className="btn btn--ghost"
            onClick={async () => {
              // Same teardown order as logout(): drop this device's
              // push token BEFORE clearing the JWT — the DELETE
              // /api/notifications/devices call needs the still-valid
              // token, and reload() would cancel an in-flight request,
              // so await it (it swallows its own errors). Codex
              // round-3 P2 on PR #422.
              try { await unregisterPush(); } catch (_) { /* ignore */ }
              try { window.Api && window.Api.logout(); } catch (_) { /* ignore */ }
              window.location.reload();
            }}
          >
            Sign out and start over
          </button>
        </p>
      </div>
    );
  }
  if (!auth) {
    if (showPricing) {
      return (
        <>
          <PricingPage onBack={closePricing} />
          <Tweaks t={t} setTweak={setTweak} />
        </>
      );
    }
    return (
      <>
        <AuthScreen
          onLogin={login}
          onSeePricing={openPricing}
          initialTab={authInitialTab}
          sessionExpired={sessionExpired}
          onSessionExpiredAck={() => setSessionExpired(false)}
        />
        <Tweaks t={t} setTweak={setTweak} />
      </>
    );
  }

  const openHistorySession = (s) => {
    setOpenChatSession(s?.id || null);
    setRoute("chat");
  };

  // ─── Pick content ────────────────────────────────────────────────────────
  const content = (() => {
    switch (route) {
      // homeowner
      case "chat": return (
        <HomeownerChat
          user={user}
          initialSessionId={openChatSession}
          onSessionStarted={() => setOpenChatSession(null)}
          onRoute={setRoute}
          onOpenBylaw={(docId) => { setOpenBylawDoc(docId); setRoute("bylaws"); }}
        />
      );
      case "history": return <HomeownerHistory onOpen={openHistorySession} />;
      case "bylaws": return (
        <BylawsLibrary
          openDocId={openBylawDoc}
          onOpenConsumed={() => setOpenBylawDoc(null)}
        />
      );
      case "directory": return (
        <CommunityDirectory
          onMessageNeighbor={(ctx) => {
            // Open the messages route with a fresh compose targeted at
            // this neighbor. Backend converts a never-talked-before
            // pairing into a PENDING message request and gates the
            // reply behind accept/deny.
            setMessagesContext({ compose: ctx });
            setRoute("messages");
          }}
        />
      );
      // manager
      case "dashboard": return <ManagerDashboard onRoute={setRoute} />;
      case "bylaws-mgmt": return <ManagerBylaws />;
      case "roster": return <ManagerRoster user={user} onUserUpdate={setUser} />;
      // PMC portfolio (company_admin only)
      case "portfolio": return <PmcPortfolio onRoute={setRoute} />;
      // admin
      case "admin-overview": return <AdminOverview />;
      case "admin-health": return <AdminHealth />;
      case "admin-communities": return <AdminCommunities user={user} />;
      case "admin-users": return <AdminUsers user={user} />;
      case "admin-revenue": return <AdminRevenue />;
      case "admin-trends": return <AdminTrends />;
      // events (shared)
      case "events": return <CommunityCalendar user={user} role={auth} />;
      case "marketplace": return (
        <Marketplace
          user={user}
          onMessageSeller={(ctx) => {
            setMessagesContext({ compose: ctx });
            setRoute("messages");
          }}
        />
      );
      case "services": return (
        <Services
          user={user}
          onMessageProvider={(ctx) => {
            setMessagesContext({ compose: ctx });
            setRoute("messages");
          }}
        />
      );
      case "assistance": return (
        <Assistance
          user={user}
          onMessageRequester={(ctx) => {
            setMessagesContext({ compose: ctx });
            setRoute("messages");
          }}
        />
      );
      case "projects": return <ProjectBuilder user={user} />;
      case "quota": return <MyQuota user={user} />;
      case "messages": return (
        <Messages
          user={user}
          openContext={messagesContext}
          onContextHandled={() => setMessagesContext(null)}
        />
      );
      case "town-square": return <TownSquare key={navTick} user={user} />;
      case "sub-communities": return (
        <SubCommunities
          key={navTick}
          user={user}
          openId={openSubCommunityId}
          onOpenId={setOpenSubCommunityId}
          onMe={() => {
            // Refresh /me so the sidebar's joined-groups list updates
            // after a create / join / leave. Swallow failures — the
            // user already saw the action succeed; sidebar will catch
            // up on next route change.
            window.Api.me().then(setUser).catch(() => {});
          }}
        />
      );
      // shared
      case "settings": return (
        <Settings
          role={auth}
          user={user}
          onUserUpdate={setUser}
          onLogout={logout}
          initialTab={settingsInitialTab}
        />
      );
      default: return <div className="scroll empty">Unknown route: {route}</div>;
    }
  })();

  const titles = routeTitles(user);
  const [title, subtitle] = titles[route] || [route, ""];

  // For chat: no padding, full-bleed composer
  const isFullBleed = route === "chat";

  return (
    <>
      <div className="app">
        <Sidebar
          role={auth}
          route={route}
          onRoute={navTo}
          onLogout={logout}
          isOpen={menuOpen}
          onClose={() => setMenuOpen(false)}
          onRole={switchRole}
          user={user}
          onSwitchCommunity={switchCommunity}
        />
        {menuOpen && (
          // Keyboard-accessible overlay: role=button + tabIndex + Enter/Space
          // handler so keyboard-only and screen-reader users can dismiss the
          // side drawer (codex P3 on #275). Pointer users still click as
          // before.
          <div
            className="side-backdrop"
            role="button"
            tabIndex={0}
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMenuOpen(false);
              }
            }}
          />
        )}
        <div className="main">
          <TopBar
            title={title}
            subtitle={subtitle}
            role={auth}
            onRole={switchRole}
            showSwitcher={t.showRoleSwitcher !== false}
            onMenu={() => setMenuOpen(true)}
            user={user}
            right={
              <NotificationsBell
                user={user}
                onUserUpdate={setUser}
                onAction={(action_route, action_payload) => {
                  if (action_route === "messages" && action_payload?.thread_id) {
                    setMessagesContext({ thread_id: action_payload.thread_id });
                  }
                  if (action_route === "settings" && action_payload?.tab) {
                    setSettingsInitialTab(action_payload.tab);
                  }
                  // Dispatch through navTo (not setRoute) so prefixed
                  // routes like "sub-community-<public_id>" hit the
                  // same deep-link handler the sidebar uses. Without
                  // this, clicking a sub-community notification set
                  // the route to the literal "sub-community-<id>"
                  // string and the SPA fell through to "Unknown
                  // route: sub-community-…".
                  if (action_route) navTo(action_route);
                }}
              />
            }
          />
          <TrialBanner
            user={user}
            onRoute={setRoute}
            onOpenBilling={() => {
              setSettingsInitialTab("billing");
              setRoute("settings");
            }}
            onUserRefresh={setUser}
          />
          <div className={isFullBleed ? "main" : ""} style={{ display: "flex", flex: 1, minHeight: 0, flexDirection: "column" }}>
            {content}
          </div>
        </div>
      </div>
      <Tweaks t={t} setTweak={setTweak} />
    </>
  );
}

function TrialBanner({ user, onRoute, onOpenBilling, onUserRefresh }) {
  const trial = user?.community?.trial;
  const role = user?.role;
  const isManager = role === "manager" || role === "company_admin" || role === "super_admin";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Gate the whole banner on subscription_tier=="trial". The billing
  // conversion path updates `subscription_tier` without clearing
  // `trial_ends_at` (it's kept as audit history), so a paid community
  // whose original trial date is in the past would otherwise see this
  // banner with a stale "trial ended / extend" CTA — and the extend
  // button would just bounce back 409 not_on_trial. Tier is the
  // source of truth for whether they're actually on a trial.
  if (user?.community?.subscription_tier !== "trial") return null;

  // Show the banner if expired OR if there are 3 or fewer days left
  // (active-trial warning).
  const daysLeft = trial?.days_remaining;
  const isExpired = !!trial?.is_expired;
  const isNearExpiry = !isExpired && typeof daysLeft === "number" && daysLeft <= 3;
  if (!isExpired && !isNearExpiry) return null;

  const extensionsRemaining = trial?.extensions_remaining ?? 0;
  const canExtend = isManager && extensionsRemaining > 0;
  // On the native shell the app must not steer users to an external
  // purchase page (Apple 3.1.3), so managers see no "Pick a plan" CTA —
  // only the free trial-extension affordance. They subscribe on the web.
  const isNative = isCapacitorNative();

  const extend = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await window.Api.billingExtendTrial();
      // Re-pull /me so the banner re-renders with the new days_remaining
      // and extension_count without a hard reload.
      const fresh = await window.Api.me();
      if (onUserRefresh) onUserRefresh(fresh);
    } catch (err) {
      setError(err.message || "Couldn't extend the trial.");
    } finally {
      setBusy(false);
    }
  };

  const tone = isExpired ? "danger" : "accent";
  const bg = tone === "danger" ? "var(--danger-soft, #fdecea)" : "var(--accent-soft, #eef3e6)";
  const fg = tone === "danger" ? "var(--danger, #b3261e)" : "var(--accent-ink, #3d4f2a)";
  const border = tone === "danger" ? "var(--danger, #b3261e)" : "color-mix(in oklab, var(--accent, #7c8a6b) 25%, transparent)";

  return (
    <div style={{
      background: bg, color: fg,
      borderBottom: `1px solid ${border}`,
      padding: "10px 16px",
      display: "flex",
      gap: 12,
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
    }}>
      <div>
        {isExpired ? (
          <>
            <b>Your free trial has ended.</b>{" "}
            {isManager
              ? (isNative
                  ? (canExtend ? "You can extend your trial below if you need more time." : "")
                  : "Pick a plan to keep using Community Compass — or grab another week if you need more time to decide.")
              : "Ask your community manager to choose a plan so Compass keeps working."}
          </>
        ) : (
          <>
            <b>{daysLeft === 0 ? "Your free trial ends today." : `Your free trial ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`}</b>{" "}
            {isManager
              ? (isNative
                  ? (canExtend ? "You can extend your trial below if you need more time." : "")
                  : "Pick a plan whenever you're ready, or grab another week first.")
              : "Your community manager will pick a plan to keep Compass running."}
          </>
        )}
        {error && (
          <div className="text-sm" style={{ marginTop: 4 }}>{error}</div>
        )}
      </div>
      <div className="row" style={{ gap: 8 }}>
        {canExtend && (
          <button
            className="btn btn--ghost btn--sm"
            onClick={extend}
            disabled={busy}
            title={
              extensionsRemaining === 1
                ? "Adds 7 days. This is your last self-serve extension."
                : `Adds 7 days. ${extensionsRemaining} extensions remaining.`
            }
          >
            {busy ? "Extending…" : "Need more time? +7 days"}
          </button>
        )}
        {isManager && !isNative && (
          <button
            className="btn btn--primary btn--sm"
            onClick={() => (onOpenBilling ? onOpenBilling() : onRoute("settings"))}
          >
            {isExpired ? "Subscribe now" : "Pick a plan"}
          </button>
        )}
      </div>
    </div>
  );
}

// CODE_REVIEW P1-7: gate the Tweaks panel mount on dev hostnames.
// The panel is host-mediated (only opens on a __activate_edit_mode
// postMessage), but it ships unconditionally to every authenticated
// user — and to the auth/pricing screens too. The wildcard '*' origin
// on its outbound postMessage means a hostile iframe parent could
// drive it. Gating on hostname keeps the panel out of production
// entirely and limits the postMessage surface to local dev.
function _tweaksAllowed() {
  if (typeof window === "undefined") return false;
  const host = window.location?.hostname || "";
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local") ||
    host.endsWith(".test") ||
    // Netlify deploy previews — useful for testing without shipping
    // the panel to the production communitycompass.ai domain.
    host.endsWith(".netlify.app")
  );
}

function Tweaks({ t, setTweak }) {
  if (!_tweaksAllowed()) return null;
  return (
    <TweaksPanel>
      <TweakSection label="Theme" />
      <TweakColor
        label="Palette"
        value={t.palette}
        options={[
          ["#c25e3a", "#2d5d4f", "#f6f1e7"],
          ["#2d5d4f", "#c25e3a", "#f6f1e7"],
          ["#1f1a14", "#c25e3a", "#f6f1e7"],
          ["#6b4c8a", "#c89b5a", "#f6f1e7"],
        ]}
        onChange={(colors) => setTweak("palette", colors)}
      />
      <TweakRadio
        label="Type pairing"
        value={t.fontPair}
        options={[
          { value: "newsreader-geist", label: "Serif + sans" },
          { value: "serif-only", label: "Serif only" },
          { value: "sans-only", label: "Sans only" },
        ]}
        onChange={(v) => setTweak("fontPair", v)}
      />
      <TweakToggle
        label="Dark mode"
        value={t.dark}
        onChange={(v) => setTweak("dark", v)}
      />

      <TweakSection label="Support" />
      <TweakToggle
        label="Show role switcher"
        value={t.showRoleSwitcher}
        onChange={(v) => setTweak("showRoleSwitcher", v)}
      />
      <TweakSelect
        label="Chat answer style"
        value={t.chatStyle}
        options={[
          { value: "formal", label: "Formal & cited" },
          { value: "neighbor", label: "Friendly neighbor" },
          { value: "concise", label: "Concise bullets" },
        ]}
        onChange={(v) => setTweak("chatStyle", v)}
      />
    </TweaksPanel>
  );
}

export { App };
